import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/content";

type Status = "idle" | "submitting" | "success" | "error";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahfu73p14a1h3mr6le0";
const MAX_LENGTH = 500;

export default function TipForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [contentLength, setContentLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setContentLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
        setContentLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试或直接发送邮件给我们。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试或直接发送邮件给我们。");
    }
  };

  return (
    <form
      data-readdy-form
      id="tip-submit-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-base font-bold text-foreground-950">爆料投稿</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
        建议附上公开可查的链接或截图，我们会先做核实，确认后再决定是否成稿。投稿可匿名。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="tip-nickname" className="mb-1.5 block text-sm font-medium text-foreground-800">
            称呼（可留空）
          </label>
          <input
            id="tip-nickname"
            name="nickname"
            type="text"
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300/80 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="tip-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="tip-email"
            name="email"
            type="email"
            required
            placeholder="便于我们核实信息"
            className="w-full rounded-md border border-background-300/80 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="tip-category" className="mb-1.5 block text-sm font-medium text-foreground-800">
          所属分类
        </label>
        <select
          id="tip-category"
          name="category"
          defaultValue=""
          className="w-full rounded-md border border-background-300/80 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors duration-200 focus:border-primary-400"
        >
          <option value="">请选择分类</option>
          {categories.map((category) => (
            <option key={category.slug} value={category.name}>
              {category.name}
            </option>
          ))}
          <option value="其他">其他 / 不确定</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="tip-links" className="mb-1.5 block text-sm font-medium text-foreground-800">
          线索链接（可留空）
        </label>
        <input
          id="tip-links"
          name="links"
          type="text"
          placeholder="公开动态链接、视频地址等"
          className="w-full rounded-md border border-background-300/80 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="tip-content" className="text-sm font-medium text-foreground-800">
            瓜料描述
          </label>
          <span className={`text-xs ${contentLength > MAX_LENGTH ? "text-primary-600" : "text-foreground-400"}`}>
            {contentLength} / {MAX_LENGTH}
          </span>
        </div>
        <textarea
          id="tip-content"
          name="content"
          required
          maxLength={MAX_LENGTH}
          rows={5}
          onChange={(event) => setContentLength(event.target.value.length)}
          placeholder="简单说清楚：发生了什么、大概什么时间、有哪些公开证据（500 字以内）"
          className="w-full resize-none rounded-md border border-background-300/80 bg-background-100/60 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="field-guard-offscreen"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className="ri-send-plane-line text-base" aria-hidden="true" />
          {status === "submitting" ? "提交中..." : "提交爆料"}
        </button>
        <p className="text-xs text-foreground-500">
          我们不会公开你的邮箱，也不会发布任何私人联系方式。
        </p>
      </div>

      {status === "success" && (
        <p
          role="status"
          className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-3 text-sm text-accent-900"
        >
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
            <i className="ri-checkbox-circle-line text-base" aria-hidden="true" />
          </span>
          投稿已收到，感谢你的线索。我们会在核实后尽快处理，如需补充信息会通过邮箱联系你。
        </p>
      )}

      {status === "error" && (
        <p
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-3 text-sm text-primary-800"
        >
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
          </span>
          {formError}
        </p>
      )}
    </form>
  );
}